import VoiCapture from "./pages/VoiCapture";
import PortalReset, { PasswordForm, PortalShell } from "./pages/portal/PortalReset";
import VoiAudit from "./pages/VoiAudit";
import SnoozeTask from "./pages/SnoozeTask";
import VoiOverrides from "./pages/VoiOverrides";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./components/AuthContext";
import { PortalAuthProvider, usePortalAuth } from "./components/PortalAuthContext";
import PortalLayout from "./components/PortalLayout";
import PortalClientLayout from "./components/PortalClientLayout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import Jobs from "./pages/Jobs";
import Time from "./pages/Time";
import Workflows from "./pages/Workflows";
import Insights from "./pages/Insights";
import FileRequests from "./pages/FileRequests";
import EsignPage from "./pages/Esign";
import Invoices from "./pages/Invoices";
import Documents from "./pages/Documents";
import EmailLog from "./pages/EmailLog";
import TeamTime from "./pages/TeamTime";
import Team from "./pages/Team";
import AcceptInvite from "./pages/AcceptInvite";
import DocumentRules from "./pages/DocumentRules";
import EmailTriage from "./pages/EmailTriage";
import EmailTemplates from "./pages/EmailTemplates";
import Leads from "./pages/Leads";
import IntakeForms from "./pages/IntakeForms";
import PublicIntakeForm from "./pages/PublicIntakeForm";
import Tasks from "./pages/Tasks";
import FeeSchedule from "./pages/FeeSchedule";
import Reports from "./pages/Reports";
import PublicIntake from "./pages/PublicIntake";
import SuperAdmin from "./pages/SuperAdmin";
import Security from "./pages/Security";
import Profile from "./pages/Profile";
import EsignDocuseal from "./pages/EsignDocuseal";
import Referrals from "./pages/Referrals";
import ReferralPartners from "./pages/ReferralPartners";
import Billing from "./pages/Billing";
import SuperadminBilling from "./pages/SuperadminBilling";
import OrganizationSettings from "./pages/OrganizationSettings";
import FirmServices from "./pages/FirmServices";
import AccountingSync from "./pages/AccountingSync";
import WorkflowSettings from "./pages/WorkflowSettings";
import Brief from "./pages/Brief";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalMagic from "./pages/portal/PortalMagic";
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalDocuments from "./pages/portal/PortalDocuments";
import { PortalFilesList, PortalFileRequest } from "./pages/portal/PortalFiles";
import { PortalSignaturesList, PortalEsignSign } from "./pages/portal/PortalEsign";
import { PortalEngagementsList, PortalEngagementSign } from "./pages/portal/PortalEngagements";
import PublicEngagement from "./pages/PublicEngagement";
import { PortalInvoicesList, PortalInvoice } from "./pages/portal/PortalInvoices";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

function Protected({ children }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--ink-3)]" data-testid="auth-loading">
        <div className="overline">Loading…</div>
      </div>
    );
  }
  if (state === false) return <Navigate to="/login" replace state={{ from: location }} />;
  return <PortalLayout>{children}</PortalLayout>;
}

function RootRouter() {
  const { state } = useAuth();
  // Google OAuth returns with #session_id=... on the root path
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (state === null) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--ink-3)]" data-testid="root-loading">
      <div className="overline">Loading…</div>
    </div>;
  }
  // Authenticated → portal. Unauthenticated → marketing landing.
  if (state) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

export default function App() {
  return (
    <AuthProvider>
      <PortalAuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" richColors />
          <Routes>
            <Route path="/" element={<RootRouter />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/dashboard" element={<DashboardRoute />} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
            <Route path="/time" element={<Protected><Time /></Protected>} />
            <Route path="/workflows" element={<Protected><Workflows /></Protected>} />
            <Route path="/insights" element={<Protected><Insights /></Protected>} />
            <Route path="/file-requests" element={<Protected><FileRequests /></Protected>} />
            <Route path="/esign" element={<Protected><EsignPage /></Protected>} />
            <Route path="/esign-docuseal" element={<Protected><EsignDocuseal /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/email-log" element={<Protected><EmailLog /></Protected>} />
            <Route path="/team-time" element={<Protected><TeamTime /></Protected>} />
            <Route path="/team" element={<Protected><Team /></Protected>} />
            <Route path="/document-rules" element={<Protected><DocumentRules /></Protected>} />
            <Route path="/email-triage" element={<Protected><EmailTriage /></Protected>} />
            <Route path="/email-templates" element={<Protected><EmailTemplates /></Protected>} />
            <Route path="/leads" element={<Protected><Leads /></Protected>} />
            <Route path="/leads/intake-forms" element={<Protected><IntakeForms /></Protected>} />
            <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
            <Route path="/referral-partners" element={<Protected><ReferralPartners /></Protected>} />
            <Route path="/f/:slug" element={<PublicIntakeForm />} />
            <Route path="/voi/:token" element={<VoiCapture />} />
            <Route path="/snooze/:token" element={<SnoozeTask />} />
            <Route path="/engage/:token" element={<PublicEngagement />} />
            <Route path="/settings/voi-audit" element={<Protected><VoiAudit /></Protected>} />
            <Route path="/settings/voi-overrides" element={<Protected><VoiOverrides /></Protected>} />
            <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
            <Route path="/fee-schedule" element={<Protected><FeeSchedule /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/intake/:slug" element={<PublicIntake />} />
            <Route path="/superadmin" element={<Protected><SuperAdmin /></Protected>} />
            <Route path="/superadmin/billing" element={<Protected><SuperadminBilling /></Protected>} />
            <Route path="/settings/security" element={<Protected><Security /></Protected>} />
            <Route path="/settings/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/settings/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/settings/organization" element={<Protected><OrganizationSettings /></Protected>} />
            <Route path="/settings/services" element={<Protected><FirmServices /></Protected>} />
            <Route path="/settings/accounting" element={<Protected><AccountingSync /></Protected>} />
            <Route path="/settings/workflow" element={<Protected><WorkflowSettings /></Protected>} />
            <Route path="/brief" element={<Brief />} />

            {/* Client portal */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/magic/:token" element={<PortalMagic />} />
            <Route path="/portal/reset/:token" element={<PortalReset />} />
            <Route path="/portal" element={<PortalProtected><PortalDashboard /></PortalProtected>} />
            <Route path="/portal/files" element={<PortalProtected><PortalFilesList /></PortalProtected>} />
            <Route path="/portal/files/:id" element={<PortalProtected><PortalFileRequest /></PortalProtected>} />
            <Route path="/portal/documents" element={<PortalProtected><PortalDocuments /></PortalProtected>} />
            <Route path="/portal/signatures" element={<PortalProtected><PortalSignaturesList /></PortalProtected>} />
            <Route path="/portal/engagements" element={<PortalProtected><PortalEngagementsList /></PortalProtected>} />
            <Route path="/portal/engagements/:id" element={<PortalProtected><PortalEngagementSign /></PortalProtected>} />
            <Route path="/portal/signatures/:id" element={<PortalProtected><PortalEsignSign /></PortalProtected>} />
            {/* Backward-compatible direct links from emails */}
            <Route path="/portal/file-requests/:id" element={<PortalProtected><PortalFileRequest /></PortalProtected>} />
            <Route path="/portal/esign/:id" element={<PortalProtected><PortalEsignSign /></PortalProtected>} />
            <Route path="/portal/invoices" element={<PortalProtected><PortalInvoicesList /></PortalProtected>} />
            <Route path="/portal/invoices/:id" element={<PortalProtected><PortalInvoice /></PortalProtected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </PortalAuthProvider>
    </AuthProvider>
  );
}

function PortalProtected({ children }) {
  const { state, setPassword } = usePortalAuth();
  const location = useLocation();
  if (state?.client_user?.must_change_password) {
    return (
      <PortalShell title="Choose a new password" sub="You signed in with a temporary password. Please set your own to continue.">
        <PasswordForm testid="portal-force-change" cta="Save password" onSubmit={setPassword} />
      </PortalShell>
    );
  }
  if (state === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--ink-3)]" data-testid="portal-loading">
        <div className="overline">Loading…</div>
      </div>
    );
  }
  if (state === false) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/portal/login?next=${next}`} replace />;
  }
  return <PortalClientLayout>{children}</PortalClientLayout>;
}

// /dashboard is the Google OAuth redirect target — render AuthCallback if a session_id is present,
// otherwise show the Dashboard (protected).
function DashboardRoute() {
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <Protected><Dashboard /></Protected>;
}
